import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, Save, Info, Check, AlertTriangle, Cloud, Ban } from 'lucide-react';
import { useFiltroMercado, FiltroMercado } from '../FiltroMercado';
import FotosResposta from './FotosResposta';
import OverlayProgresso from './OverlayProgresso';
import useAutosaveRascunho from '../../../utils/useAutosaveRascunho';

const API_BASE = '/api/operacional/aval360';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

// Ponteiro local do rascunho em andamento — usado para RETOMAR ao reabrir o hub.
const LS_RASCUNHO_360 = 'aval360_rascunho';
const guardarPonteiroRascunho = (info) => {
  try { localStorage.setItem(LS_RASCUNHO_360, JSON.stringify(info)); } catch { /* ignora */ }
};
const limparPonteiroRascunho = () => {
  try { localStorage.removeItem(LS_RASCUNHO_360); } catch { /* ignora */ }
};

// ⚠️ Réplica da régua NOVA do backend (operacional/aval360/aval360-score.js) para
// feedback ao vivo. O backend recalcula e é a FONTE DA VERDADE — mudou a régua lá,
// ajuste aqui. Verde ≥90 · Amarelo 80–89,99 · Laranja 70–79,99 · Vermelho <70.
// Recebe o percentual em fração (0..1). (Quanto MAIOR o %, melhor.)
const classificar360 = (p) => {
  if (p >= 0.90) return { nome: 'Verde', cor: '#059669', bg: '#d1fae5', icone: '🟢' };
  if (p >= 0.80) return { nome: 'Amarelo', cor: '#ca8a04', bg: '#fef9c3', icone: '🟡' };
  if (p >= 0.70) return { nome: 'Laranja', cor: '#ea580c', bg: '#ffedd5', icone: '🟠' };
  return { nome: 'Vermelho', cor: '#dc2626', bg: '#fee2e2', icone: '🔴' };
};
// Estilo da área/total sem nada aplicável (tudo N/A) — fora do cálculo.
const CONF_NA = { nome: 'N/A', cor: '#475569', bg: '#f1f5f9', icone: '⚪' };

const MAX_FOTOS_PERGUNTA = 3; // espelha o teto do backend

const Aval360Questionario = ({ idVersao, idRascunho = null, onVoltar }) => {
  const [versao, setVersao] = useState(null);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [idAvaliacao, setIdAvaliacao] = useState(idRascunho);
  const [jaFinalizada, setJaFinalizada] = useState(false);
  const [chave] = useState(() => `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);

  const cabecalhoVazio = () => ({
    loja: '', data_avaliacao: new Date().toISOString().substring(0, 10),
    avaliador_responsavel: '', gerente_plantao: '', gerente_negocios: '',
    observacao_geral: '',
  });
  const [cabecalho, setCabecalho] = useState(cabecalhoVazio());
  const [gerentesPorLoja, setGerentesPorLoja] = useState({});

  // Filtro de mercado (cross-avaliação): consultor designado fica travado no
  // mercado do OUTRO; perfil amplo escolhe (Todos/Norte/Sul). Só UX — o backend
  // é o portão. Ver operacional/FiltroMercado + operacional/consultor-mercado.js.
  const { travaMercado, filtroMercado, setFiltroMercado, lojas } = useFiltroMercado();

  // respostas[idPergunta] = { resposta: 'SIM'|'NAO'|'NA', fotos: [url], observacao }
  const [respostas, setRespostas] = useState({});

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const [rVersao, rGerentes] = await Promise.all([
        fetch(`${API_BASE}/versao/${idVersao}`, { headers: auth() }),
        fetch(`${API_BASE}/gerentes`, { headers: auth() }),
      ]);
      if (!rVersao.ok) throw new Error('Falha ao carregar versão.');
      const data = await rVersao.json();
      setVersao(data.versao);
      setAreas(data.areas || []);
      if (rGerentes.ok) setGerentesPorLoja(await rGerentes.json());

      if (idRascunho) {
        const rr = await fetch(`${API_BASE}/avaliacao/${idRascunho}`, { headers: auth() });
        const d = rr.ok ? await rr.json() : null;
        if (d?.avaliacao) {
          const a = d.avaliacao;
          setIdAvaliacao(a.ID);
          if (a.STATUS === 'Finalizado') setJaFinalizada(true);
          setCabecalho({
            loja: a.LOJA || '',
            data_avaliacao: a.DATA_AVALIACAO ? String(a.DATA_AVALIACAO).slice(0, 10) : new Date().toISOString().substring(0, 10),
            avaliador_responsavel: a.AVALIADOR_RESPONSAVEL || '',
            gerente_plantao: a.GERENTE_PLANTAO || '',
            gerente_negocios: a.GERENTE_NEGOCIOS || '',
            observacao_geral: a.OBSERVACAO_GERAL || '',
          });
          const resp = {};
          (d.respostas || []).forEach((r) => {
            resp[r.ID_PERGUNTA] = {
              resposta: r.RESPOSTA,
              observacao: r.OBSERVACAO || '',
              fotos: (r.FOTOS || []).filter(Boolean),
            };
          });
          setRespostas(resp);
        }
      }
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }, [idVersao, idRascunho]);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (idRascunho) return;
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setCabecalho((c) => ({ ...c, avaliador_responsavel: c.avaliador_responsavel || user.nome || '' }));
  }, [idRascunho]);

  // Nota em tempo real, por área e total — CONTAGEM pura (SIM ÷ (SIM+NÃO)).
  // Pergunta N/A: fora do denominador.
  const resumo = useMemo(() => {
    let simTotal = 0, aplicTotal = 0;
    const porArea = areas.map((a) => {
      let sim = 0, aplic = 0;
      for (const p of a.perguntas) {
        const r = respostas[p.ID];
        if (!r || !r.resposta || r.resposta === 'NA') continue; // não respondida ou N/A: fora
        aplic += 1;
        if (r.resposta === 'SIM') sim += 1;
      }
      simTotal += sim; aplicTotal += aplic;
      if (aplic === 0) return { idArea: a.ID, sim: 0, aplic: 0, pct: 0, na: true, classe: CONF_NA };
      const pct = sim / aplic;
      return { idArea: a.ID, sim, aplic, pct, na: false, classe: classificar360(pct) };
    });
    const pctTotal = aplicTotal > 0 ? simTotal / aplicTotal : 0;
    return {
      simTotal, aplicTotal, pctTotal, porArea,
      classTotal: aplicTotal > 0 ? classificar360(pctTotal) : CONF_NA,
    };
  }, [areas, respostas]);

  const totalPerguntas = useMemo(
    () => areas.reduce((acc, a) => acc + a.perguntas.length, 0),
    [areas]
  );
  const totalRespondidas = useMemo(() => {
    let n = 0;
    for (const a of areas) for (const p of a.perguntas) {
      const r = respostas[p.ID];
      if (r && (r.resposta === 'SIM' || r.resposta === 'NAO' || r.resposta === 'NA')) n++;
    }
    return n;
  }, [areas, respostas]);
  const pctProgresso = totalPerguntas > 0 ? Math.round((totalRespondidas / totalPerguntas) * 100) : 0;

  const responder = (idPergunta, resposta) => {
    setRespostas((r) => ({ ...r, [idPergunta]: { ...r[idPergunta], resposta } }));
  };
  const setFotos = (idPergunta, fotos) =>
    setRespostas((r) => ({ ...r, [idPergunta]: { ...r[idPergunta], fotos } }));
  const setObservacao = (idPergunta, observacao) =>
    setRespostas((r) => ({ ...r, [idPergunta]: { ...r[idPergunta], observacao } }));

  // Marca/desmarca a ÁREA INTEIRA como N/A (segmento que a loja não tem). Só nas
  // áreas com PERMITE_NA_AREA. Marcar → todas as perguntas viram N/A; desmarcar →
  // limpa as respostas da área.
  const areaEstaToda = (a, valor) =>
    a.perguntas.length > 0 && a.perguntas.every((p) => respostas[p.ID]?.resposta === valor);
  const alternarAreaNa = (a) => {
    const jaToda = areaEstaToda(a, 'NA');
    setRespostas((r) => {
      const novo = { ...r };
      for (const p of a.perguntas) {
        if (jaToda) novo[p.ID] = { ...novo[p.ID], resposta: undefined };
        else novo[p.ID] = { ...novo[p.ID], resposta: 'NA' };
      }
      return novo;
    });
  };

  const montarPayload = (rascunho) => ({
    ...(idAvaliacao ? { id_avaliacao: idAvaliacao } : {}),
    id_versao: idVersao,
    loja: cabecalho.loja || null,
    data_avaliacao: cabecalho.data_avaliacao,
    avaliador_responsavel: cabecalho.avaliador_responsavel || null,
    gerente_negocios: cabecalho.gerente_negocios || null,
    gerente_plantao: cabecalho.gerente_plantao || null,
    observacao_geral: cabecalho.observacao_geral || null,
    respostas: Object.entries(respostas)
      .filter(([, r]) => r && ['SIM', 'NAO', 'NA'].includes(r.resposta))
      .map(([id, r]) => {
        const ehNao = r.resposta === 'NAO';
        return {
          id_pergunta: Number(id),
          resposta: r.resposta,
          observacao: ehNao ? (r.observacao || null) : null,
          fotos: ehNao ? (r.fotos || []).filter(Boolean) : [],
        };
      }),
    ...(rascunho ? { rascunho: true } : {}),
  });

  const salvarRascunho = async () => {
    const r = await fetch(`${API_BASE}/submit`, {
      method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify(montarPayload(true)),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data?.message || 'Erro ao salvar rascunho.');
    if (data.idAvaliacao && data.idAvaliacao !== idAvaliacao) setIdAvaliacao(data.idAvaliacao);
    guardarPonteiroRascunho({
      id: data.idAvaliacao, idVersao,
      versao: versao?.NOME_VERSAO || '', loja: cabecalho.loja || '',
    });
  };

  const snapshot = useMemo(() => JSON.stringify({ cabecalho, respostas }), [cabecalho, respostas]);
  const autosaveAtivo = !loading && !!cabecalho.loja && !jaFinalizada && !salvando;
  const { estado: autosaveEstado } = useAutosaveRascunho({ ativo: autosaveAtivo, snapshot, salvarFn: salvarRascunho });

  // Perguntas "Não" sem foto OU sem observação — evidência é obrigatória nos DOIS.
  const naoIncompleto = useMemo(() => {
    const lista = [];
    for (const a of areas) for (const p of a.perguntas) {
      const r = respostas[p.ID];
      if (r && r.resposta === 'NAO') {
        const semFoto = !(r.fotos || []).filter(Boolean).length;
        const semObs = !(r.observacao || '').trim();
        if (semFoto || semObs) lista.push(p.TEXTO);
      }
    }
    return lista;
  }, [areas, respostas]);

  const submeter = async () => {
    if (!cabecalho.loja || !cabecalho.data_avaliacao) {
      setErro('Loja e data são obrigatórios.');
      return;
    }
    if (totalRespondidas < totalPerguntas) {
      setErro(`Responda todas as ${totalPerguntas} perguntas antes de salvar. (${totalRespondidas}/${totalPerguntas})`);
      return;
    }
    if (naoIncompleto.length > 0) {
      setErro(`Toda pergunta "Não" precisa de foto E observação. Faltam ${naoIncompleto.length}.`);
      return;
    }
    setSalvando(true);
    setErro('');
    setSucesso('');
    try {
      const r = await fetch(`${API_BASE}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth() },
        body: JSON.stringify(montarPayload(false)),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.message || 'Falha ao salvar.');
      limparPonteiroRascunho();
      setSucesso(`Avaliação #${data.idAvaliacao} salva — ${data.totalSim}/${data.totalSim + data.totalNao} (${data.percentual}%) · ${data.classificacao}`);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      setCabecalho({ ...cabecalhoVazio(), avaliador_responsavel: user.nome || '' });
      setRespostas({});
      setIdAvaliacao(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setErro(e.message);
    } finally {
      setSalvando(false);
    }
  };

  if (loading) return <p style={{ color: '#64748b' }}>Carregando formulário…</p>;
  if (erro && !versao) return <p style={{ color: '#dc2626' }}>{erro}</p>;

  const gerentesDaLoja = cabecalho.loja ? gerentesPorLoja[cabecalho.loja] : null;

  const botaoResp = (selecionado, cor) => ({
    flex: 1, padding: '8px 12px', borderRadius: 8,
    border: `2px solid ${selecionado ? cor : '#e2e8f0'}`,
    background: selecionado ? cor : '#fff',
    color: selecionado ? '#fff' : '#475569',
    fontWeight: 700, cursor: 'pointer', fontSize: 13, transition: 'all .12s',
  });

  return (
    <div>
      {salvando && <OverlayProgresso titulo="Salvando avaliação…" subtitulo="Gerando o PDF — pode levar alguns segundos." />}
      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onVoltar} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', background: '#fff', border: '1px solid #e2e8f0',
          borderRadius: 8, fontWeight: 700, cursor: 'pointer',
        }}>
          <ArrowLeft size={16} /> Voltar
        </button>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: '#0f172a' }}>
          {versao?.NOME_VERSAO}
        </h3>
      </div>

      {jaFinalizada && (
        <div style={{ padding: 12, background: '#fef3c7', color: '#92400e', borderRadius: 8, marginBottom: 12, fontWeight: 700 }}>
          Esta avaliação já foi finalizada — não pode mais ser editada.
        </div>
      )}
      {erro && (
        <div style={{ padding: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, marginBottom: 12 }}>{erro}</div>
      )}
      {sucesso && (
        <div style={{ padding: 12, background: '#d1fae5', color: '#065f46', borderRadius: 8, marginBottom: 12, fontWeight: 700 }}>✅ {sucesso}</div>
      )}

      {/* Cabeçalho */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h4 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 800, color: '#475569' }}>Dados da Avaliação</h4>
        <FiltroMercado
          ativo
          travaMercado={travaMercado}
          filtroMercado={filtroMercado}
          onChange={(m) => {
            setFiltroMercado(m);
            // Troca de mercado zera a loja (e os gerentes) — a antiga pode ter
            // saído da lista visível.
            setCabecalho((c) => ({ ...c, loja: '', gerente_negocios: '', gerente_plantao: '' }));
          }}
        />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
          <div>
            <label style={lbl}>Loja *</label>
            <select
              value={cabecalho.loja}
              onChange={(e) => setCabecalho({ ...cabecalho, loja: e.target.value, gerente_negocios: '', gerente_plantao: '' })}
              style={inp}
            >
              <option value="">— escolher —</option>
              {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Data *</label>
            <input type="date" value={cabecalho.data_avaliacao}
              onChange={(e) => setCabecalho({ ...cabecalho, data_avaliacao: e.target.value })} style={inp} />
          </div>
          <div>
            <label style={lbl}>Responsável (Avaliador)</label>
            <input type="text" value={cabecalho.avaliador_responsavel}
              onChange={(e) => setCabecalho({ ...cabecalho, avaliador_responsavel: e.target.value })} style={inp} />
          </div>
          <div>
            <label style={lbl}>Gerente de Negócios</label>
            <select value={cabecalho.gerente_negocios}
              onChange={(e) => setCabecalho({ ...cabecalho, gerente_negocios: e.target.value })}
              disabled={!gerentesDaLoja} style={inp}>
              <option value="">{gerentesDaLoja ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {(gerentesDaLoja?.negocios || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div>
            <label style={lbl}>Gerente de Plantão</label>
            <select value={cabecalho.gerente_plantao}
              onChange={(e) => setCabecalho({ ...cabecalho, gerente_plantao: e.target.value })}
              disabled={!gerentesDaLoja} style={inp}>
              <option value="">{gerentesDaLoja ? '— escolher —' : 'Escolha a loja primeiro'}</option>
              {(gerentesDaLoja?.plantao || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Nota em tempo real + progresso + autosave (sticky) */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 5,
        background: resumo.classTotal.bg,
        border: `2px solid ${resumo.classTotal.cor}`,
        borderRadius: 12, padding: 16, marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 28 }}>{resumo.classTotal.icone}</div>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', letterSpacing: 0.5 }}>RESULTADO GERAL</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: resumo.classTotal.cor }}>
              {resumo.simTotal} / {resumo.aplicTotal} · {(resumo.pctTotal * 100).toFixed(2)}% · {resumo.classTotal.nome}
            </div>
          </div>
          {autosaveAtivo && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700,
              color: autosaveEstado === 'erro' ? '#b91c1c' : autosaveEstado === 'salvando' ? '#64748b' : '#059669',
            }}>
              {autosaveEstado === 'salvando' ? <><Cloud size={14} /> Salvando rascunho…</>
                : autosaveEstado === 'salvo' ? <><Check size={14} /> Rascunho salvo</>
                  : autosaveEstado === 'erro' ? <><AlertTriangle size={14} /> Falha ao salvar rascunho</>
                    : <><Cloud size={14} /> Rascunho automático ligado</>}
            </span>
          )}
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>
            <span>Progresso</span>
            <span>{totalRespondidas} / {totalPerguntas} · {pctProgresso}%</span>
          </div>
          <div style={{ height: 8, background: 'rgba(255,255,255,.6)', borderRadius: 999, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${pctProgresso}%`, borderRadius: 999,
              background: resumo.classTotal.cor, transition: 'width .2s',
            }} />
          </div>
        </div>
      </div>

      {/* Áreas */}
      {areas.map((a, idx) => {
        const r = resumo.porArea[idx];
        const areaToda = areaEstaToda(a, 'NA');
        let subsecaoAtual = null;
        return (
          <div key={a.ID} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: '#0f172a' }}>
                {a.CODIGO ? <span style={{ color: '#64748b' }}>{a.CODIGO} · </span> : null}{a.NOME}
              </h4>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {/* Marcar a ÁREA INTEIRA como N/A — só nos segmentos (PERMITE_NA_AREA). */}
                {a.PERMITE_NA_AREA ? (
                  <button onClick={() => alternarAreaNa(a)} title="A loja não tem este segmento — marca a área inteira como N/A"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8,
                      border: `1px solid ${areaToda ? '#64748b' : '#cbd5e1'}`, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                      background: areaToda ? '#64748b' : '#fff', color: areaToda ? '#fff' : '#475569',
                    }}>
                    <Ban size={14} /> {areaToda ? 'Segmento N/A (desfazer)' : 'Loja não tem este segmento'}
                  </button>
                ) : null}
                <div style={{
                  padding: '4px 10px', borderRadius: 8, fontSize: 13, fontWeight: 800,
                  background: r.classe.bg, color: r.classe.cor,
                }}>
                  {r.na ? `${r.classe.icone} N/A` : `${r.classe.icone} ${r.sim}/${r.aplic} · ${(r.pct * 100).toFixed(0)}%`}
                </div>
              </div>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              {a.perguntas.map((p) => {
                const resp = respostas[p.ID] || {};
                const ehNao = resp.resposta === 'NAO';
                const semFoto = ehNao && !(resp.fotos || []).filter(Boolean).length;
                const semObs = ehNao && !(resp.observacao || '').trim();
                // Cabeçalho de subseção (rótulo visual do QSL) quando muda.
                let cabSub = null;
                if (p.SUBSECAO && p.SUBSECAO !== subsecaoAtual) {
                  subsecaoAtual = p.SUBSECAO;
                  cabSub = (
                    <div key={`sub-${p.ID}`} style={{
                      fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase',
                      letterSpacing: 0.5, margin: '6px 0 2px',
                    }}>{p.SUBSECAO}</div>
                  );
                }
                return (
                  <React.Fragment key={p.ID}>
                    {cabSub}
                    <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', gap: 12, alignItems: 'center' }}>
                        <div>
                          <div style={{ color: '#0f172a', fontSize: 14 }}>
                            {/* Sequencial estilo PDF (QSL-01), DERIVADO de área+ORDEM — não é o código opaco. */}
                            <span style={{ fontWeight: 700, color: '#64748b', marginRight: 6 }}>
                              {a.CODIGO}-{String(p.ORDEM).padStart(2, '0')}
                            </span>
                            {p.TEXTO}
                          </div>
                          {resp.resposta === 'NA' && (
                            <div style={{ marginTop: 6, fontSize: 12, fontWeight: 700, color: '#64748b' }}>
                              ⚪ Não aplicável — fora do cálculo.
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button onClick={() => responder(p.ID, 'SIM')} style={botaoResp(resp.resposta === 'SIM', '#059669')}>Sim</button>
                          <button onClick={() => responder(p.ID, 'NAO')} style={botaoResp(resp.resposta === 'NAO', '#dc2626')}>Não</button>
                          <button onClick={() => responder(p.ID, 'NA')} style={botaoResp(resp.resposta === 'NA', '#64748b')}>N/A</button>
                        </div>
                      </div>

                      {/* Evidência OBRIGATÓRIA (foto + observação) — só quando "Não". */}
                      {ehNao && (
                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e2e8f0', display: 'grid', gap: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: semFoto ? '#b91c1c' : '#475569' }}>
                              {semFoto ? '📷 Foto obrigatória' : '📷 Fotos da evidência'} ({(resp.fotos || []).filter(Boolean).length}/{MAX_FOTOS_PERGUNTA})
                            </span>
                            <FotosResposta
                              fotos={resp.fotos} onChange={(f) => setFotos(p.ID, f)} max={MAX_FOTOS_PERGUNTA}
                              loja={cabecalho.loja} chave={chave} prefixoSlot={`p${p.ID}`}
                            />
                          </div>
                          <textarea
                            rows={2} value={resp.observacao || ''}
                            onChange={(e) => setObservacao(p.ID, e.target.value)}
                            placeholder="Observação obrigatória"
                            style={{
                              width: '100%', padding: 8, borderRadius: 6, fontFamily: 'inherit', fontSize: 13,
                              border: `1px solid ${semObs ? '#fca5a5' : '#cbd5e1'}`,
                            }}
                          />
                          {semObs && <span style={{ fontSize: 12, color: '#b91c1c', fontWeight: 700 }}>Observação obrigatória no “Não”.</span>}
                        </div>
                      )}
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Observação geral */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 16 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 800, color: '#475569' }}>Observações Gerais</h4>
        <textarea
          rows={4} value={cabecalho.observacao_geral}
          onChange={(e) => setCabecalho({ ...cabecalho, observacao_geral: e.target.value })}
          placeholder="Anotações gerais sobre a visita (opcional)."
          style={{ width: '100%', padding: 10, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit' }}
        />
        <div style={{
          marginTop: 12, display: 'flex', alignItems: 'flex-start', gap: 10,
          background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10,
          padding: '12px 14px', color: '#1e40af', fontSize: 13,
        }}>
          <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            Cada pergunta marcada <strong>“Não”</strong> exige <strong>uma foto E uma observação</strong> da
            evidência. Áreas de segmento (Quiosque/Delivery/Drive) podem ser marcadas <strong>N/A inteiras</strong>.
            O preenchimento é <strong>salvo automaticamente</strong> — você pode sair e continuar depois.
          </span>
        </div>
      </div>

      <button onClick={submeter} disabled={salvando || jaFinalizada} style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '12px 24px', background: (salvando || jaFinalizada) ? '#94a3b8' : '#059669',
        color: '#fff', border: 'none', borderRadius: 8,
        fontWeight: 800, fontSize: 15, cursor: (salvando || jaFinalizada) ? 'not-allowed' : 'pointer',
      }}>
        <Save size={18} /> {salvando ? 'Salvando…' : 'Finalizar Avaliação'}
      </button>
    </div>
  );
};

const lbl = { fontSize: 12, fontWeight: 700, color: '#64748b' };
const inp = { width: '100%', padding: 8, marginTop: 4, border: '1px solid #cbd5e1', borderRadius: 6 };

export default Aval360Questionario;
